import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useMentionSearch from "./useMentionSearch";
import useStaffSearch from "./useStaffSearch";
import useRotatingPlaceholder from "./useRotatingPlaceholder";
import MentionPopover from "./MentionPopover";

const EXAMPLE_PROMPTS = [
  { 
    prompt: "E.g. Send the current statement as a pdf" },
  {
    prompt: "E.g. John can pay his balance weekly over 12 weeks, starting tomorrow",
  },
  {
    prompt: "E.g. Tim wants a copy of Inv 521561 and it's Proof of delivery",
  },
  { 
    prompt: "E.g. Chase all overdue invoices" },
  {
    prompt: "E.g. Has their credit code changed recently?",
  },
  {
    prompt: "E.g. Lisa promised to pay the £643 on Tuesday",
  },
  { 
    prompt: "E.g. Send the first chaser" },
  {
    prompt: "E.g. Add a note saying I called Chris and he's on holiday till next month",
  },
  {
    prompt: "E.g. On stop after filing insolvency, block all purchases!!",
  },
  {
    prompt: "E.g. Send a pr for £65"
  },
  {
    prompt: "Type @ to notify a colleague in a note"
  },
];

// Guiding phrases (not examples, so no "E.g." prefix) surfaced regularly between
// the examples to reinforce what the assistant is for.
const PRIMARY_PROMPTS = [
  "How can I help you today?"
];

// Capture everything after the "@" up to the caret, including spaces, so
// multi-word account/staff names can be searched (selection ends the mention).
const MENTION_RE = /(?:^|\s)@([^@\n]*)$/;

// Serialise for the LLM: mention tokens become <colleague>Full Name</colleague>
// so the model gets an explicit, unambiguous reference; other text is verbatim.
function serializeForLlm(node) {
  let out = "";
  node.childNodes.forEach((child) => {
    if (child.nodeType === Node.TEXT_NODE) {
      out += child.textContent;
    } else if (child.nodeType === Node.ELEMENT_NODE) {
      if (child.dataset?.userId) {
        const name = (child.textContent ?? "").replace(/^@/, "");
        out += `<colleague>${name}</colleague>`;
      } else {
        out += serializeForLlm(child);
      }
    }
  });
  return out;
}

const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// Resolve the caret to the text node it sits in and the offset within it. Typing
// the first character into an empty contentEditable can leave the caret anchored
// to the editor element rather than a text node (browser-dependent), so fall back
// to the text node adjacent to the caret — otherwise a leading "@" (the first
// character of a fresh note) would never open the mention popover.
function resolveCaretTextNode(editor, range) {
  const { startContainer, startOffset } = range;
  if (startContainer.nodeType === Node.TEXT_NODE) {
    return editor.contains(startContainer)
      ? { node: startContainer, offset: startOffset }
      : null;
  }
  if (startContainer === editor || editor.contains(startContainer)) {
    const before = startContainer.childNodes[startOffset - 1];
    if (before && before.nodeType === Node.TEXT_NODE) {
      return { node: before, offset: before.textContent.length };
    }
    const after = startContainer.childNodes[startOffset];
    if (after && after.nodeType === Node.TEXT_NODE) {
      return { node: after, offset: 0 };
    }
  }
  return null;
}

function buildMentionToken(id, name) {
  const token = document.createElement("span");
  token.setAttribute("contenteditable", "false");
  token.setAttribute("data-user-id", id);
  token.textContent = `@${name}`;
  // Inline styles avoid Tailwind purging classes off a dynamic element.
  token.style.color = "#2563eb";
  token.style.fontWeight = "500";
  token.style.whiteSpace = "nowrap";
  return token;
}

// Rebuild mention tokens when restoring text so tags survive an Edit round-trip.
function seedEditorContent(editor, text, mentions) {
  editor.textContent = "";
  if (!mentions || mentions.length === 0) {
    editor.textContent = text;
    return;
  }
  const idByToken = new Map(mentions.map((m) => [`@${m.name}`, m.id]));
  const pattern = [...idByToken.keys()]
    .sort((a, b) => b.length - a.length) // longest first so multi-word names win
    .map(escapeRegExp)
    .join("|");
  text.split(new RegExp(`(${pattern})`, "g")).forEach((part) => {
    if (!part) return;
    if (idByToken.has(part)) {
      editor.appendChild(buildMentionToken(idByToken.get(part), part.slice(1)));
    } else {
      editor.appendChild(document.createTextNode(part));
    }
  });
}

// Must match the promptInUp/promptOutUp animation duration in tailwind.config.js.
const PLACEHOLDER_ROTATE_MS = 450;

// Carousel between placeholder values: the outgoing text slides up and fades out
// while the incoming one rises in from below. Both layers are clipped to one line.
function RotatingPlaceholder({ value }) {
  const [current, setCurrent] = useState(value);
  const [previous, setPrevious] = useState(null);

  useEffect(() => {
    if (value === current) return undefined;
    setPrevious(current);
    setCurrent(value);
    const timeoutId = setTimeout(() => setPrevious(null), PLACEHOLDER_ROTATE_MS);
    return () => clearTimeout(timeoutId);
  }, [value, current]);

  return (
    <>
      {previous !== null && (
        <span
          key={`out-${previous}`}
          className="absolute inset-x-0 top-0 block animate-promptOutUp truncate"
        >
          {previous}
        </span>
      )}
      <span
        key={`in-${current}`}
        className="absolute inset-x-0 top-0 block animate-promptInUp truncate"
      >
        {current}
      </span>
    </>
  );
}

export default function MentionTextarea({
  onChange,
  onCustomerSelect,
  selectedCustomer,
  onSubmit,
  onTab,
  onSuggestionArrow,
  onSuggestionEnter,
  onSuggestionEscape,
  activePreview = null,
  initialText = "",
  initialMentions = [],
  bare = false,
  popoverPlacement = "below",
  placeholder = "Ask Ontik",
  accountPlaceholder = "Select an account to add a note or send a payment request",
  examplePrompts = EXAMPLE_PROMPTS,
  readLoading = false,
  loadingPlaceholder = "",
}) {
  const editorRef = useRef(null);
  const containerRef = useRef(null);
  const mentionRangeRef = useRef(null);
  const seededRef = useRef(false);
  // While an autocomplete suggestion is "active" (hovered/arrowed), the editor shows the full suggestion
  // (Chrome-omnibox style). This holds the user's real content so arrowing away restores it.
  const previewBaseRef = useRef(null);
  // Last account the seed/cleanup effect acted on, so it only fires on a genuine
  // account change — not on the re-renders caused by an unstable onChange, which
  // would otherwise wipe the lone "@" the user is typing and shut the picker.
  const prevCustomerIdRef = useRef(undefined);
  // Mirrors popover open state without making detectMention depend on
  // mentionQuery — that dependency re-created the account-guard effect on every
  // keystroke and instantly closed the popover once an account was selected.
  const popoverOpenRef = useRef(false);

  const [isEmpty, setIsEmpty] = useState(true);
  // True when the field holds nothing but the account "@" (no prompt text yet).
  const [accountPromptEmpty, setAccountPromptEmpty] = useState(true);
  const [mentionQuery, setMentionQuery] = useState(null); // null ⇒ popover closed
  const [activeFilter, setActiveFilter] = useState(null); // null | "customer" | "staff"
  const [activeIndex, setActiveIndex] = useState(0);

  // Until an account is chosen, the "@" can only pick an account (no staff).
  const accountOnly = !selectedCustomer;

  const showPromptPlaceholder = Boolean(selectedCustomer) && isEmpty;
  // A hovered suggestion previews as grey ghost text — only while the composer is empty, and it takes
  // the place of the rotating example prompt. Nothing is inserted and no request is made.
  const promptItems = useMemo(
    () => [
      ...PRIMARY_PROMPTS.map((text) => ({ text, primary: true })),
      ...examplePrompts.map((example) => ({ text: example.prompt, primary: false })),
    ],
    [examplePrompts],
  );
  const rotatingPrompt = useRotatingPlaceholder(
    promptItems,
    showPromptPlaceholder && !readLoading,
  );

  const popoverOpen = mentionQuery !== null;
  const query = mentionQuery ?? "";
  const { results: customerResults, isFetching: customersFetching } =
    useMentionSearch(query, {
      enabled: popoverOpen && activeFilter !== "staff",
    });
  const { results: staffResults } = useStaffSearch(query, {
    enabled: popoverOpen && !accountOnly && activeFilter !== "customer",
  });

  const displayResults = useMemo(() => {
    const customers = customerResults.map((c) => ({ ...c, _type: "customer" }));
    if (accountOnly) return customers;
    const staff = staffResults.map((s) => ({ ...s, _type: "staff" }));
    if (activeFilter === "customer") return customers;
    if (activeFilter === "staff") return staff;
    return [...customers, ...staff];
  }, [customerResults, staffResults, activeFilter, accountOnly]);

  const isFetching = customersFetching && activeFilter !== "staff";

  // On mount, seed any restored text (used when "Edit" returns a prompt to the
  // box) and focus the editor.
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;
    if (initialText) {
      seedEditorContent(editor, initialText, initialMentions);
      const selection = window.getSelection();
      const end = document.createRange();
      end.selectNodeContents(editor);
      end.collapse(false);
      selection.removeAllRanges();
      selection.addRange(end);
      reportChange();
    }
    editor.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Chrome-omnibox preview: when a suggestion is active, show its full text in the editor; when it clears,
  // restore the user's own content. The draft (onChange) is NOT updated here, so Enter still sends the
  // active suggestion and arrowing away leaves what the user actually typed untouched.
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;
    const placeCaretAtEnd = () => {
      const selection = window.getSelection();
      const range = document.createRange();
      range.selectNodeContents(editor);
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    };
    if (activePreview != null) {
      if (previewBaseRef.current === null) previewBaseRef.current = editor.innerHTML;
      editor.textContent = activePreview;
      placeCaretAtEnd();
      setIsEmpty(false);
      setAccountPromptEmpty(false);
    } else if (previewBaseRef.current !== null) {
      editor.innerHTML = previewBaseRef.current;
      previewBaseRef.current = null;
      placeCaretAtEnd();
      const text = (editor.textContent ?? "").trim();
      setIsEmpty(text.length === 0);
      setAccountPromptEmpty(text.length === 0 || text === "@");
    }
  }, [activePreview]);

  useEffect(() => {
    setActiveIndex(0);
  }, [mentionQuery, activeFilter]);

  const closePopover = useCallback(() => {
    mentionRangeRef.current = null;
    popoverOpenRef.current = false;
    setMentionQuery(null);
    setActiveFilter(null);
  }, []);

  const handleFilterChange = (type) =>
    setActiveFilter((current) => (current === type ? null : type));

  const reportChange = useCallback(() => {
    const editor = editorRef.current;
    if (!editor) return;
    const text = editor.textContent ?? "";
    const mentionedCustomerIds = [
      ...editor.querySelectorAll("[data-customer-id]"),
    ].map((el) => el.dataset.customerId);
    const userTokens = [...editor.querySelectorAll("[data-user-id]")];
    const mentionedUserIds = userTokens.map((el) => el.dataset.userId);
    const mentions = userTokens.map((el) => ({
      id: el.dataset.userId,
      name: (el.textContent ?? "").replace(/^@/, ""),
    }));
    const llmText = serializeForLlm(editor);
    const trimmed = text.trim();
    setIsEmpty(trimmed.length === 0);
    setAccountPromptEmpty(trimmed.length === 0 || trimmed === "@");
    onChange?.({ text, mentionedCustomerIds, mentionedUserIds, mentions, llmText });
  }, [onChange]);

  const detectMention = useCallback(() => {
    const editor = editorRef.current;
    const selection = window.getSelection();
    if (!editor || !selection || selection.rangeCount === 0) {
      closePopover();
      return;
    }
    const range = selection.getRangeAt(0);
    const caret = resolveCaretTextNode(editor, range);
    if (!selection.isCollapsed || !caret) {
      closePopover();
      return;
    }
    const { node, offset } = caret;

    const textUpToCaret = node.textContent.slice(0, offset);
    const match = MENTION_RE.exec(textUpToCaret);
    if (!match) {
      closePopover();
      return;
    }

    const query = match[1];
    const atIndex = offset - query.length - 1; // position of "@"
    const mentionRange = document.createRange();
    mentionRange.setStart(node, atIndex);
    mentionRange.setEnd(node, offset);
    mentionRangeRef.current = mentionRange;
    // On open (popover was closed), default the filter: once an account is
    // selected an "@" most likely tags a colleague, so default to Staff.
    if (!popoverOpenRef.current) {
      setActiveFilter(selectedCustomer ? "staff" : "customer");
    }
    popoverOpenRef.current = true;
    setMentionQuery(query);
  }, [closePopover, selectedCustomer]);

  // Open the account picker by inserting an "@" (at the end of any existing text,
  // or at the start when empty) and defaulting the popover to accounts. Used to
  // force an account to be chosen on open and again whenever one is removed.
  const startAccountMention = useCallback(() => {
    const editor = editorRef.current;
    if (!editor) return;
    editor.focus();

    const selection = window.getSelection();
    const end = document.createRange();
    end.selectNodeContents(editor);
    end.collapse(false);
    selection.removeAllRanges();
    selection.addRange(end);

    const text = editor.textContent ?? "";
    if (!text.endsWith("@")) {
      // execCommand keeps the contentEditable's own cursor/undo handling intact.
      const prefix = text.length > 0 && !text.endsWith(" ") ? " @" : "@";
      document.execCommand("insertText", false, prefix);
    }

    setActiveFilter("customer");
    detectMention();
  }, [detectMention]);

  // Always have an account being chosen or selected: seed an "@" on open and
  // re-seed (at the end, preserving the prompt) whenever the account is cleared.
  useEffect(() => {
    const customerId = selectedCustomer?.id ?? null;
    if (customerId === prevCustomerIdRef.current) return;
    prevCustomerIdRef.current = customerId;
    if (selectedCustomer) {
      seededRef.current = false;
      // A customer set programmatically (e.g. opening from a task's "view context") can leave the
      // seeded "@" and its picker behind — the account wasn't chosen through the picker — so clear
      // a lone "@" and close the popover, leaving the composer empty and ready to type.
      const editor = editorRef.current;
      if (editor && (editor.textContent ?? "").trim() === "@") {
        editor.textContent = "";
        reportChange();
      }
      closePopover();
      return;
    }
    if (seededRef.current) return;
    seededRef.current = true;
    startAccountMention();
  }, [selectedCustomer, startAccountMention, closePopover, reportChange]);

  const insertMention = useCallback(
    (item) => {
      const editor = editorRef.current;
      const range = mentionRangeRef.current;
      if (!editor || !range) return;

      range.deleteContents();
      const selection = window.getSelection();

      if (item._type === "customer") {
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
        editor.focus();
        closePopover();
        reportChange();
        onCustomerSelect?.(item);
        return;
      }

      const token = buildMentionToken(item.id, item.name);

      const trailingSpace = document.createTextNode(" ");
      const fragment = document.createDocumentFragment();
      fragment.appendChild(token);
      fragment.appendChild(trailingSpace);
      range.insertNode(fragment);

      // Place the caret just after the inserted space.
      const caret = document.createRange();
      caret.setStartAfter(trailingSpace);
      caret.collapse(true);
      selection.removeAllRanges();
      selection.addRange(caret);
      editor.focus();

      closePopover();
      reportChange();
    },
    [closePopover, reportChange, onCustomerSelect],
  );

  // The account "@" is mandatory until one is picked — if it's been deleted, put
  // it straight back so an account can't be skipped. Re-inserting is deferred
  // because execCommand is unreliable while an input/keyup event is in flight.
  const enforceAccountMention = useCallback(() => {
    if (selectedCustomer) return;
    if ((editorRef.current?.textContent ?? "").includes("@")) return;
    setTimeout(() => {
      if (selectedCustomer) return;
      if ((editorRef.current?.textContent ?? "").includes("@")) return;
      startAccountMention();
    }, 0);
  }, [selectedCustomer, startAccountMention]);

  const handleInput = () => {
    // Typing over a suggestion preview commits whatever is shown as the real content (don't restore the
    // pre-preview text when the active suggestion clears).
    previewBaseRef.current = null;
    reportChange();
    detectMention();
    enforceAccountMention();
  };

  // Move the caret detection after the browser updates the selection.
  const handleSelectionChange = () => {
    detectMention();
    enforceAccountMention();
  };

  const handleKeyDown = (e) => {
    // With the mention popover closed, the keys drive the autocomplete-suggestion panel (Google-style):
    // arrows move the highlight, Enter sends the highlighted one (else submits the composer), Tab fills it.
    if (!popoverOpen) {
      if ((e.key === "ArrowDown" || e.key === "ArrowUp") && onSuggestionArrow?.(e.key === "ArrowDown" ? 1 : -1)) {
        e.preventDefault();
        return;
      }
      if (e.key === "Tab" && !e.shiftKey && onTab?.()) {
        e.preventDefault();
        return;
      }
      if (e.key === "Escape" && onSuggestionEscape?.()) {
        e.preventDefault();
        return;
      }
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        if (!onSuggestionEnter?.()) onSubmit?.();
        return;
      }
      return;
    }
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setActiveIndex((i) =>
          Math.min(i + 1, Math.max(displayResults.length - 1, 0)),
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
        break;
      case "Enter":
        if (displayResults[activeIndex]) {
          e.preventDefault();
          insertMention(displayResults[activeIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        closePopover();
        break;
      default:
        break;
    }
  };

  // Close the popover when clicking outside the input card.
  useEffect(() => {
    if (!popoverOpen) return undefined;
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        closePopover();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [popoverOpen, closePopover]);

  return (
    // "above" leaves this unpositioned so the popover anchors to the caller's composer card instead.
    <div ref={containerRef} className={popoverPlacement === "above" ? undefined : "relative"}>
      <div
        className={
          bare
            ? ""
            : "rounded-lg border border-zinc-300 p-3 focus-within:outline focus-within:outline-1 focus-within:-outline-offset-1 focus-within:outline-primary"
        }
      >
        <div className="relative">
          <div
            ref={editorRef}
            contentEditable
            suppressContentEditableWarning
            role="textbox"
            aria-multiline="true"
            aria-label="Note"
            onInput={handleInput}
            onKeyDown={handleKeyDown}
            onKeyUp={handleSelectionChange}
            onMouseUp={handleSelectionChange}
            className="min-h-[1.5rem] w-full whitespace-pre-wrap break-words text-sm text-zinc-900 focus:outline-none"
          />
          {/* No account yet, and no partial prompt: hint sits just after the "@". */}
          {!selectedCustomer && accountPromptEmpty && (
            <span className="pointer-events-none absolute left-4 top-0 text-sm text-zinc-400">
              {accountPlaceholder}
            </span>
          )}
          {/* Account selected: a steady hint while the read loads, then the prompts
              rotate through with a fade-up carousel once it's ready. */}
          {showPromptPlaceholder && (
            <span className="pointer-events-none absolute inset-x-0 top-0 block h-6 max-w-full overflow-hidden text-sm leading-6 text-zinc-400">
              {readLoading ? (
                <span className="block truncate">{loadingPlaceholder}</span>
              ) : promptItems.length > 0 ? (
                <RotatingPlaceholder value={rotatingPrompt} />
              ) : (
                <span className="block truncate">{placeholder}</span>
              )}
            </span>
          )}
        </div>

      </div>

      {popoverOpen && (
        <MentionPopover
          results={displayResults}
          isFetching={isFetching}
          activeFilter={activeFilter}
          onFilterChange={handleFilterChange}
          activeIndex={activeIndex}
          onHoverIndex={setActiveIndex}
          onSelect={insertMention}
          showFilters={!accountOnly}
          placement={popoverPlacement}
        />
      )}
    </div>
  );
}
